import type { Risonanza } from './risonanze';

/**
 * Bestiario originale dei Lumini: creature bioluminescenti nate dalle
 * anomalie magnetiche delle Faglie di Lumen.
 */

export interface BaseStats {
  hp: number;
  atk: number;
  def: number;
  spd: number;
}

/** Parametri per la generazione procedurale dello sprite (stile flat pastello). */
export interface SpeciesLook {
  body: string; // colore corpo
  accent: string; // marcature
  glow: string; // bioluminescenza
  shape: 'tondo' | 'gocciola' | 'felino' | 'roccioso' | 'serpentino' | 'alato';
}

export interface SpeciesDef {
  id: string;
  name: string;
  risonanza: Risonanza;
  baseStats: BaseStats;
  /** Mosse apprese, in ordine di livello di apprendimento. */
  learnset: { level: number; moveId: string }[];
  description: string;
  look: SpeciesLook;
  /** Specie incontrabile allo stato selvatico nella Valle. */
  wild: boolean;
}

const defs: SpeciesDef[] = [
  // ——— Starter ———
  {
    id: 'brillio',
    name: 'Brillio',
    risonanza: 'aurora',
    baseStats: { hp: 44, atk: 11, def: 10, spd: 13 },
    learnset: [
      { level: 1, moveId: 'zampata' },
      { level: 1, moveId: 'lampo-prismatico' },
      { level: 7, moveId: 'sciame-lucente' },
      { level: 12, moveId: 'velo-boreale' },
    ],
    description:
      'Uno spirito azzurro le cui pinne rifrangono la luce in arcobaleni. Nasce solo dove due faglie si incrociano.',
    look: { body: '#bfe8f7', accent: '#7db8d9', glow: '#fff6d6', shape: 'felino' },
    wild: false,
  },
  {
    id: 'cinerino',
    name: 'Cinerino',
    risonanza: 'brace',
    baseStats: { hp: 46, atk: 13, def: 11, spd: 10 },
    learnset: [
      { level: 1, moveId: 'zampata' },
      { level: 1, moveId: 'soffio-di-cenere' },
      { level: 7, moveId: 'scaglia-di-quarzo' },
      { level: 12, moveId: 'cuore-di-magma' },
    ],
    description:
      'Una salamandra di cenere compressa: il suo dorso cova braci che si ravvivano quando è felice.',
    look: { body: '#f5b58a', accent: '#d97d5a', glow: '#ffd98a', shape: 'serpentino' },
    wild: false,
  },
  {
    id: 'plinfa',
    name: 'Plinfa',
    risonanza: 'marea',
    baseStats: { hp: 48, atk: 10, def: 13, spd: 11 },
    learnset: [
      { level: 1, moveId: 'zampata' },
      { level: 1, moveId: 'stilla-lucente' },
      { level: 7, moveId: 'pulsazione' },
      { level: 12, moveId: 'onda-cristallina' },
    ],
    description:
      'Una goccia di rugiada condensata attorno a un nucleo magnetico. Riflette il cielo come uno specchio.',
    look: { body: '#a8d8f0', accent: '#6bb8e8', glow: '#e8f7ff', shape: 'gocciola' },
    wild: false,
  },
  // ——— Selvatici comuni ———
  {
    id: 'sassolite',
    name: 'Sassolite',
    risonanza: 'terra',
    baseStats: { hp: 50, atk: 11, def: 14, spd: 7 },
    learnset: [
      { level: 1, moveId: 'zampata' },
      { level: 1, moveId: 'scaglia-di-quarzo' },
      { level: 10, moveId: 'eruzione-silicea' },
    ],
    description:
      'Una palla di pelo bruno che ingoia ciottoli magnetici. Rotola controvento quando fiuta una faglia.',
    look: { body: '#cdbaa0', accent: '#a08a6b', glow: '#d6f5c0', shape: 'roccioso' },
    wild: true,
  },
  {
    id: 'voltilla',
    name: 'Voltilla',
    risonanza: 'volt',
    baseStats: { hp: 40, atk: 12, def: 9, spd: 15 },
    learnset: [
      { level: 1, moveId: 'zampata' },
      { level: 1, moveId: 'scintilla-vagante' },
      { level: 10, moveId: 'arco-di-faglia' },
    ],
    description:
      'Le sue antenne raccolgono le cariche statiche dell’aria. Sfreccia tra i cespugli lasciando scie dorate.',
    look: { body: '#f5e8a0', accent: '#d9b85a', glow: '#fffbe0', shape: 'felino' },
    wild: true,
  },
  {
    id: 'ecora',
    name: 'Ecora',
    risonanza: 'eco',
    baseStats: { hp: 44, atk: 10, def: 10, spd: 12 },
    learnset: [
      { level: 1, moveId: 'zampata' },
      { level: 1, moveId: 'pulsazione' },
      { level: 10, moveId: 'canto-della-cava' },
    ],
    description:
      'Un piccolo alato senza occhi che "vede" cantando. Il suo richiamo notturno calma le faglie irrequiete.',
    look: { body: '#d8c0f0', accent: '#a88ad9', glow: '#f0e0ff', shape: 'alato' },
    wild: true,
  },
  {
    id: 'lucciolfo',
    name: 'Lucciolfo',
    risonanza: 'aurora',
    baseStats: { hp: 42, atk: 12, def: 9, spd: 14 },
    learnset: [
      { level: 1, moveId: 'zampata' },
      { level: 1, moveId: 'lampo-prismatico' },
      { level: 10, moveId: 'sciame-lucente' },
    ],
    description:
      'Uno spiritello dorato che pulsa come una lanterna. I branchi di Lucciolfo segnano i sentieri sicuri.',
    look: { body: '#e0f0f7', accent: '#9ad7f5', glow: '#fff6d6', shape: 'felino' },
    wild: true,
  },
  // ——— Selvatici da anomalia (più rari) ———
  {
    id: 'bramuca',
    name: 'Bramuca',
    risonanza: 'brace',
    baseStats: { hp: 47, atk: 14, def: 10, spd: 9 },
    learnset: [
      { level: 1, moveId: 'soffio-di-cenere' },
      { level: 1, moveId: 'zampata' },
      { level: 11, moveId: 'cuore-di-magma' },
    ],
    description:
      'Un bruco corazzato che digerisce minerali ferrosi. Quando muta, lascia gusci ancora tiepidi.',
    look: { body: '#f0a07d', accent: '#c45a3a', glow: '#ffcf8a', shape: 'serpentino' },
    wild: true,
  },
  {
    id: 'marevia',
    name: 'Marevia',
    risonanza: 'marea',
    baseStats: { hp: 46, atk: 11, def: 12, spd: 11 },
    learnset: [
      { level: 1, moveId: 'stilla-lucente' },
      { level: 1, moveId: 'zampata' },
      { level: 11, moveId: 'onda-cristallina' },
    ],
    description:
      'Una medusa d’acqua dolce che fluttua tra le pozze della Valle seguendo le linee del campo magnetico.',
    look: { body: '#8ac8e8', accent: '#5a98c4', glow: '#d0f0ff', shape: 'alato' },
    wild: true,
  },
];

export const SPECIES: ReadonlyMap<string, SpeciesDef> = new Map(defs.map((s) => [s.id, s]));

export const STARTER_IDS = ['brillio', 'cinerino', 'plinfa'] as const;

export function getSpecies(id: string): SpeciesDef {
  const species = SPECIES.get(id);
  if (!species) throw new Error(`Specie sconosciuta: ${id}`);
  return species;
}

export function wildSpeciesIds(): string[] {
  return [...SPECIES.values()].filter((s) => s.wild).map((s) => s.id);
}
